import React from 'react';
import './FeatureSection.css';
import { PhoneMockup, useScrollAnimation } from '../shared';

/**
 * Feature Section component
 * Displays a feature with title, description, sub-features and phone mockup
 * Includes scroll-triggered animation for accessibility-friendly reveal effect
 * @param {React.ReactNode} title - Main feature title (can include UnderlinedText)
 * @param {string} description - Feature description
 * @param {Array} subFeatures - Array of sub-feature objects with title and description
 * @param {string} phoneImage - Image source for phone mockup
 * @param {string} phoneAlt - Alt text for phone mockup
 * @param {boolean} reversed - Whether to reverse the layout
 * @param {string} id - Section ID for navigation
 */
const FeatureSection = ({
  title,
  description,
  subFeatures = [],
  phoneImage,
  phoneAlt,
  reversed = false,
  id
}) => {
  const titleId = `${id}-heading`;

  // Use shared scroll animation hook - re-triggers on each scroll into viewport
  const { ref: sectionRef, isVisible } = useScrollAnimation({
    rootMargin: '-100px 0px',
    threshold: 0.1,
    delay: 100
  });

  const sectionClasses = [
    'feature-section',
    reversed ? 'feature-section--reversed' : '',
    isVisible ? 'is-visible' : ''
  ].filter(Boolean).join(' ');

  return (
    <section
      ref={sectionRef}
      className={sectionClasses}
      id={id}
      aria-labelledby={titleId}
    >
      <div className="feature-section-container">
        {/* Phone Mockup */}
        <div className="feature-section-phone">
          <PhoneMockup imageSrc={phoneImage} imageAlt={phoneAlt} />
        </div>

        {/* Content */}
        <div className="feature-section-content">
          <div className="feature-section-text-block">
            {/* Title - underlines applied via UnderlinedText in title prop */}
            <h2 id={titleId} className="feature-section-title">
              {title}
            </h2>

            {/* Description */}
            <p className="feature-section-description">
              {description}
            </p>

            {/* Sub-features */}
            {subFeatures.map((feature, index) => (
              <div className="feature-section-subfeature" key={index}>
                <h3 className="feature-section-subfeature-title">
                  {feature.title}
                </h3>
                <p className="feature-section-subfeature-description">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSection;
